import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { SERVER_URL } from "../../../config/config";
import { JackpotAssetType, JackpotRoundState } from "./jackpot.enums";
import { JACKPOT_INVENTORY_ITEM_PRICE } from "../config/jackpot.constants";
import {
  approveJackpotAsset,
  calculateWinner,
  claimFees,
  enterPot,
  enterPotWithInventoryItem,
  enterPotWithPerkBoxes,
  getAssetsPrice,
  getBetLimits,
  getCurrentRound,
  getCurrentRoundFeeAmount,
  getCurrentRoundPerkBoxItemIds,
  getCurrentRoundTotalPotUSD,
  getRoundAmounts,
  getTotalAccumulatedFees,
  setBetLimits,
} from "../api/MafiaJackpot";
import { JackpotBid } from "./JackpotBid";
import { UserProfile } from "../../../types/UserProfile";

export interface JackpotState {
  betLimits: {
    minimumBetUSD: number;
    maximumBetUSD: number;
  };
  totalAccumulatedFees: {
    accumulatedMafiaFees: number;
    accumulatedCashFees: number;
    accumulatedCreditFees: number;
  };

  currentRoundTotalPotUSD: number;
  currentRoundFeeAmount: number;
  currentRoundPerkBoxItemIds: number[];
  currentRound: {
    roundId: number;
    state: JackpotRoundState;
    totalUSD: number;
    liveTime: number;
    duration: number;
    entriesCount: number;
    minBetUSD: number;
    maxBetUSD: number;
  };
  currentRoundInfo: {
    id: number;
    roundId: number;

    winnerInfo: {
      winner: string;
      winnerEntriesTotalUSD: number;
      roundTotalUSD: number;

      winnerUserData: UserProfile;
    };
    winner: string;
    roundTotalUSD: number;
    totalUSDValue: number;
    winnerEntriesTotalUSD: number;
    feeUSDAmount: number;
    status: string;
    timestamp: number;
    block: number;
    bids: JackpotBid[];
  };

  roundAmounts: {
    mafiaAmount: number;
    inGameCashAmount: number;
    helperCreditAmount: number;
    perkBoxAmount: number;
    ogCrateAmount: number;
  };
  assetsPrice: {
    [JackpotAssetType.OG_CRATE]: number;
    [JackpotAssetType.PERK_BOX]: number;
    [JackpotAssetType.MAFIA_TOKEN]: number;
    [JackpotAssetType.IN_GAME_CASH]: number;
    [JackpotAssetType.HELPER_CREDIT]: number;
  };
  inventoryItemPrices: number[];

  // Round history state
  roundHistory: {
    rounds: Array<{
      id: number;
      winner: string;
      winnerUser: {
        address: string;
        name: string;
        imageId: number;
        city: number;
      };
      roundTotalUSD: number;
      winnerEntriesTotalUSD: number;
      feeUSDAmount: number;
      status: string;
      timestamp: number;
      block: number;
      totalUSDValue: number;
      txHash: string;

      bids: Array<JackpotBid>;
    }>;
    hasMore: boolean;
    currentPage: number;
    pageSize: number;
  };

  isGettingBetLimits: boolean;
  isGettingTotalAccumulatedFees: boolean;
  isGettingCurrentRoundTotalPotUSD: boolean;
  isGettingCurrentRoundFeeAmount: boolean;
  isGettingCurrentRoundPerkBoxItemIds: boolean;
  isGettingCurrentRound: boolean;
  isGettingRoundAmounts: boolean;
  isGettingAssetsPrice: boolean;
  isEnteringPotWithPerkBoxes: boolean;
  isEnteringPot: boolean;
  isCalculatingWinner: boolean;
  isApprovingJackpotAsset: boolean;
  isGettingCurrentRoundInfo: boolean;
  isGettingRoundHistory: boolean;
  isSettingBetLimits: boolean;
  isEnteringPotWithInventoryItem: boolean;
  isClaimingFees: boolean;
}

const initialState: JackpotState = {
  betLimits: {
    minimumBetUSD: 0,
    maximumBetUSD: 0,
  },
  totalAccumulatedFees: {
    accumulatedMafiaFees: 0,
    accumulatedCashFees: 0,
    accumulatedCreditFees: 0,
  },
  currentRoundTotalPotUSD: 0,
  currentRoundFeeAmount: 0,
  currentRoundPerkBoxItemIds: [],
  currentRound: {
    roundId: 0,
    state: JackpotRoundState.WAITING,
    totalUSD: 0,
    liveTime: 0,
    duration: 0,
    entriesCount: 0,
    minBetUSD: 0,
    maxBetUSD: 0,
  },
  currentRoundInfo: {
    id: 0,
    roundId: 0,
    winnerInfo: {
      winner: "",
      winnerEntriesTotalUSD: 0,
      roundTotalUSD: 0,
      winnerUserData: {
        address: "",
      },
    },
    winner: "",
    roundTotalUSD: 0,
    totalUSDValue: 0,
    winnerEntriesTotalUSD: 0,
    feeUSDAmount: 0,
    status: "",
    timestamp: 0,
    block: 0,
    bids: [],
  },
  roundHistory: {
    rounds: [],
    hasMore: true,
    currentPage: 1,
    pageSize: 10,
  },
  roundAmounts: {
    mafiaAmount: 0,
    inGameCashAmount: 0,
    helperCreditAmount: 0,
    perkBoxAmount: 0,
    ogCrateAmount: 0,
  },
  assetsPrice: {
    [JackpotAssetType.MAFIA_TOKEN]: 0,
    [JackpotAssetType.IN_GAME_CASH]: 0,
    [JackpotAssetType.HELPER_CREDIT]: 0,
    [JackpotAssetType.OG_CRATE]: 0,
    [JackpotAssetType.PERK_BOX]: 0,
  },
  inventoryItemPrices: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  isGettingBetLimits: false,
  isGettingTotalAccumulatedFees: false,
  isGettingCurrentRoundTotalPotUSD: false,
  isGettingCurrentRoundFeeAmount: false,
  isGettingCurrentRoundPerkBoxItemIds: false,
  isGettingCurrentRound: false,
  isGettingRoundAmounts: false,
  isGettingAssetsPrice: false,
  isEnteringPotWithPerkBoxes: false,
  isCalculatingWinner: false,
  isApprovingJackpotAsset: false,
  isEnteringPot: false,
  isGettingCurrentRoundInfo: false,
  isGettingRoundHistory: false,
  isSettingBetLimits: false,
  isEnteringPotWithInventoryItem: false,
  isClaimingFees: false,
};

export const getCurrentRoundInfoAction = createAsyncThunk(
  "jackpot/getCurrentRoundInfoAction",
  async ({ roundId }: { roundId: number }) => {
    const response = await axios.get(
      `${SERVER_URL}/business/jackpot/${roundId}/${roundId}`
    );
    return response.data;
  }
);

export const getRoundHistoryAction = createAsyncThunk(
  "jackpot/getRoundHistoryAction",
  async ({
    from,
    to,
    append = false,
  }: {
    from: number;
    to: number;
    append?: boolean;
  }) => {
    const response = await axios.get(
      `${SERVER_URL}/business/jackpot/${from}/${to}`
    );
    return { data: response.data, append };
  }
);

export const getBetLimitsAction = createAsyncThunk(
  "jackpot/getBetLimitsAction",
  async () => {
    const betLimits = await getBetLimits();
    return betLimits;
  }
);

export const getTotalAccumulatedFeesAction = createAsyncThunk(
  "jackpot/getTotalAccumulatedFeesAction",
  async () => {
    const totalAccumulatedFees = await getTotalAccumulatedFees();
    return totalAccumulatedFees;
  }
);

export const getCurrentRoundTotalPotUSDAction = createAsyncThunk(
  "jackpot/getCurrentRoundTotalPotUSDAction",
  async () => {
    const currentRoundTotalPotUSD = await getCurrentRoundTotalPotUSD();
    return currentRoundTotalPotUSD;
  }
);

export const getCurrentRoundFeeAmountAction = createAsyncThunk(
  "jackpot/getCurrentRoundFeeAmountAction",
  async () => {
    const currentRoundFeeAmount = await getCurrentRoundFeeAmount();
    return currentRoundFeeAmount;
  }
);

export const getCurrentRoundPerkBoxItemIdsAction = createAsyncThunk(
  "jackpot/getCurrentRoundPerkBoxItemIdsAction",
  async () => {
    const currentRoundPerkBoxItemIds = await getCurrentRoundPerkBoxItemIds();
    return currentRoundPerkBoxItemIds;
  }
);

export const getCurrentRoundAction = createAsyncThunk(
  "jackpot/getCurrentRoundAction",
  async () => {
    const currentRound = await getCurrentRound();
    return currentRound;
  }
);

export const getRoundAmountsAction = createAsyncThunk(
  "jackpot/getRoundAmountsAction",
  async (roundId: number) => {
    const roundAmounts = await getRoundAmounts(roundId);
    return roundAmounts;
  }
);

export const getAssetsPriceAction = createAsyncThunk(
  "jackpot/getAssetsPriceAction",
  async () => {
    const assetsPrice = await getAssetsPrice();
    return assetsPrice;
  }
);

export const enterPotAction = createAsyncThunk(
  "jackpot/enterPotAction",
  async ({
    entryType,
    amount,
    account,
  }: {
    entryType: JackpotAssetType;
    amount: number;
    account: string;
  }) => {
    const tx = await enterPot(entryType, amount, account);
    return tx.transactionHash;
  }
);

export const enterPotWithPerkBoxesAction = createAsyncThunk(
  "jackpot/enterPotWithPerkBoxesAction",
  async ({
    perkBoxItemIds,
    account,
  }: {
    perkBoxItemIds: number[];
    account: string;
  }) => {
    const tx = await enterPotWithPerkBoxes(perkBoxItemIds, account);
    return tx.transactionHash;
  }
);

export const calculateWinnerAction = createAsyncThunk(
  "jackpot/calculateWinnerAction",
  async ({ account }: { account: string }) => {
    const tx = await calculateWinner(account);
    return tx.transactionHash;
  }
);

export const approveJackpotAssetAction = createAsyncThunk(
  "jackpot/approveJackpotAssetAction",
  async ({
    asset,
    amount,
    account,
  }: {
    asset: JackpotAssetType;
    amount: number;
    account: string;
  }) => {
    await approveJackpotAsset(asset, amount, account);
  }
);

export const setBetLimitsAction = createAsyncThunk(
  "jackpot/setBetLimitsAction",
  async ({
    minimumBetUSD,
    maximumBetUSD,
    account,
  }: {
    minimumBetUSD: number;
    maximumBetUSD: number;
    account: string;
  }) => {
    const tx = await setBetLimits(minimumBetUSD, maximumBetUSD, account);
    return tx.transactionHash;
  }
);

export const enterPotWithInventoryItemAction = createAsyncThunk(
  "jackpot/enterPotWithInventoryItemAction",
  async ({ itemId, account }: { itemId: number; account: string }) => {
    const tx = await enterPotWithInventoryItem(itemId, account);
    return tx.transactionHash;
  }
);

export const claimFeesAction = createAsyncThunk(
  "jackpot/claimFeesAction",
  async ({ account }: { account: string }) => {
    const tx = await claimFees(account);
    return tx;
  }
);

export const jackpotSlice = createSlice({
  name: "jackpot",
  initialState,
  reducers: {
    addNewBid: (state, action) => {
      state.currentRoundInfo.bids.push(action.payload);
      state.currentRound.totalUSD += action.payload.usdValue;
      state.currentRoundInfo.totalUSDValue += action.payload.usdValue;
      state.currentRound.entriesCount += 1;

      // Ensure only unique userAddress values are present in bids
      const uniqueBidsMap = new Map();
      state.currentRoundInfo.bids.forEach((bid) => {
        uniqueBidsMap.set(bid.userAddress, bid);
      });

      if (uniqueBidsMap.size > 1) {
        state.currentRound.state = JackpotRoundState.LIVE;
      }
    },
    updateWinner: (state, action) => {
      state.currentRoundInfo.winnerInfo = action.payload;
    },

    clearCurrentRoundInfo: (state) => {
      state.currentRoundInfo = initialState.currentRoundInfo;
    },
  },
  extraReducers(builder) {
    builder.addCase(getBetLimitsAction.pending, (state) => {
      state.isGettingBetLimits = true;
    });
    builder.addCase(getBetLimitsAction.fulfilled, (state, action) => {
      state.isGettingBetLimits = false;
      state.betLimits = {
        minimumBetUSD: action.payload.minimumBetUSD,
        maximumBetUSD: action.payload.maximumBetUSD,
      };
    });
    builder.addCase(getBetLimitsAction.rejected, (state) => {
      state.isGettingBetLimits = false;
    });
    builder.addCase(getTotalAccumulatedFeesAction.pending, (state) => {
      state.isGettingTotalAccumulatedFees = true;
    });
    builder.addCase(
      getTotalAccumulatedFeesAction.fulfilled,
      (state, action) => {
        state.isGettingTotalAccumulatedFees = false;
        state.totalAccumulatedFees = action.payload;
      }
    );
    builder.addCase(getTotalAccumulatedFeesAction.rejected, (state) => {
      state.isGettingTotalAccumulatedFees = false;
    });
    builder.addCase(getCurrentRoundTotalPotUSDAction.pending, (state) => {
      state.isGettingCurrentRoundTotalPotUSD = true;
    });
    builder.addCase(
      getCurrentRoundTotalPotUSDAction.fulfilled,
      (state, action) => {
        state.isGettingCurrentRoundTotalPotUSD = false;
        state.currentRoundTotalPotUSD = action.payload;
      }
    );
    builder.addCase(getCurrentRoundTotalPotUSDAction.rejected, (state) => {
      state.isGettingCurrentRoundTotalPotUSD = false;
    });
    builder.addCase(getCurrentRoundFeeAmountAction.pending, (state) => {
      state.isGettingCurrentRoundFeeAmount = true;
    });
    builder.addCase(
      getCurrentRoundFeeAmountAction.fulfilled,
      (state, action) => {
        state.isGettingCurrentRoundFeeAmount = false;
        state.currentRoundFeeAmount = action.payload;
      }
    );
    builder.addCase(getCurrentRoundFeeAmountAction.rejected, (state) => {
      state.isGettingCurrentRoundFeeAmount = false;
    });
    builder.addCase(getCurrentRoundPerkBoxItemIdsAction.pending, (state) => {
      state.isGettingCurrentRoundPerkBoxItemIds = true;
    });
    builder.addCase(
      getCurrentRoundPerkBoxItemIdsAction.fulfilled,
      (state, action) => {
        state.isGettingCurrentRoundPerkBoxItemIds = false;
        state.currentRoundPerkBoxItemIds = action.payload;
      }
    );
    builder.addCase(getCurrentRoundPerkBoxItemIdsAction.rejected, (state) => {
      state.isGettingCurrentRoundPerkBoxItemIds = false;
    });
    builder.addCase(getCurrentRoundAction.pending, (state) => {
      state.isGettingCurrentRound = true;
    });
    builder.addCase(getCurrentRoundAction.fulfilled, (state, action) => {
      state.isGettingCurrentRound = false;
      state.currentRound = action.payload;
    });
    builder.addCase(getCurrentRoundAction.rejected, (state) => {
      state.isGettingCurrentRound = false;
    });
    builder.addCase(getRoundAmountsAction.pending, (state) => {
      state.isGettingRoundAmounts = true;
    });
    builder.addCase(getRoundAmountsAction.fulfilled, (state, action) => {
      state.isGettingRoundAmounts = false;
      state.roundAmounts = action.payload;
    });
    builder.addCase(getRoundAmountsAction.rejected, (state) => {
      state.isGettingRoundAmounts = false;
    });
    builder.addCase(getAssetsPriceAction.pending, (state) => {
      state.isGettingAssetsPrice = true;
    });
    builder.addCase(getAssetsPriceAction.fulfilled, (state, action) => {
      state.isGettingAssetsPrice = false;
      state.assetsPrice = {
        [JackpotAssetType.MAFIA_TOKEN]:
          action.payload[JackpotAssetType.MAFIA_TOKEN],
        [JackpotAssetType.IN_GAME_CASH]:
          action.payload[JackpotAssetType.IN_GAME_CASH],
        [JackpotAssetType.HELPER_CREDIT]:
          action.payload[JackpotAssetType.HELPER_CREDIT],
        [JackpotAssetType.OG_CRATE]: action.payload[JackpotAssetType.OG_CRATE],
        [JackpotAssetType.PERK_BOX]: action.payload[JackpotAssetType.PERK_BOX],
      };
      state.inventoryItemPrices = JACKPOT_INVENTORY_ITEM_PRICE.map((price) => {
        return price * action.payload[JackpotAssetType.IN_GAME_CASH];
      });
    });
    builder.addCase(getAssetsPriceAction.rejected, (state) => {
      state.isGettingAssetsPrice = false;
    });
    builder.addCase(enterPotAction.pending, (state) => {
      state.isEnteringPot = true;
    });
    builder.addCase(enterPotAction.fulfilled, (state, action) => {
      state.isEnteringPot = false;
      state.currentRound = {
        ...state.currentRound,
        entriesCount: state.currentRound.entriesCount + 1,
      };
    });
    builder.addCase(enterPotAction.rejected, (state) => {
      state.isEnteringPot = false;
    });
    builder.addCase(enterPotWithPerkBoxesAction.pending, (state) => {
      state.isEnteringPotWithPerkBoxes = true;
    });
    builder.addCase(enterPotWithPerkBoxesAction.fulfilled, (state, action) => {
      state.isEnteringPotWithPerkBoxes = false;
      state.currentRound = {
        ...state.currentRound,
        entriesCount: state.currentRound.entriesCount + 1,
      };
    });
    builder.addCase(
      enterPotWithPerkBoxesAction.rejected,
      (state, { error }) => {
        state.isEnteringPotWithPerkBoxes = false;

        console.log(error);
      }
    );
    builder.addCase(calculateWinnerAction.pending, (state) => {
      state.isCalculatingWinner = true;
    });
    builder.addCase(calculateWinnerAction.fulfilled, (state, action) => {
      state.isCalculatingWinner = false;
    });
    builder.addCase(calculateWinnerAction.rejected, (state) => {
      state.isCalculatingWinner = false;
    });
    builder.addCase(approveJackpotAssetAction.pending, (state) => {
      state.isApprovingJackpotAsset = true;
    });
    builder.addCase(approveJackpotAssetAction.fulfilled, (state) => {
      state.isApprovingJackpotAsset = false;
    });
    builder.addCase(approveJackpotAssetAction.rejected, (state) => {
      state.isApprovingJackpotAsset = false;
    });
    builder.addCase(setBetLimitsAction.pending, (state) => {
      state.isSettingBetLimits = true;
    });
    builder.addCase(setBetLimitsAction.fulfilled, (state) => {
      state.isSettingBetLimits = false;
    });
    builder.addCase(setBetLimitsAction.rejected, (state) => {
      state.isSettingBetLimits = false;
    });
    builder.addCase(getCurrentRoundInfoAction.pending, (state) => {
      state.isGettingCurrentRoundInfo = true;
    });
    builder.addCase(getCurrentRoundInfoAction.fulfilled, (state, action) => {
      state.isGettingCurrentRoundInfo = false;
      state.currentRoundInfo = action.payload[0]
        ? {
            ...action.payload[0],
            winnerInfo: state.currentRoundInfo.winnerInfo,
          }
        : {
            ...initialState.currentRoundInfo,
            winnerInfo: state.currentRoundInfo.winnerInfo,
          };
    });
    builder.addCase(getCurrentRoundInfoAction.rejected, (state) => {
      state.isGettingCurrentRoundInfo = false;
    });
    builder.addCase(getRoundHistoryAction.pending, (state) => {
      state.isGettingRoundHistory = true;
    });
    builder.addCase(getRoundHistoryAction.fulfilled, (state, action) => {
      state.isGettingRoundHistory = false;

      if (action.payload.append) {
        // Append new rounds to existing history
        state.roundHistory.rounds = [
          ...state.roundHistory.rounds,
          ...action.payload.data,
        ];
      } else {
        // Replace existing history
        state.roundHistory.rounds = action.payload.data;
      }

      // Update pagination
      state.roundHistory.currentPage += 1;
      state.roundHistory.hasMore =
        action.payload.data.length === state.roundHistory.pageSize;
    });
    builder.addCase(getRoundHistoryAction.rejected, (state) => {
      state.isGettingRoundHistory = false;
    });
    builder.addCase(enterPotWithInventoryItemAction.pending, (state) => {
      state.isEnteringPotWithInventoryItem = true;
    });
    builder.addCase(
      enterPotWithInventoryItemAction.fulfilled,
      (state, action) => {
        state.isEnteringPotWithInventoryItem = false;
      }
    );
    builder.addCase(enterPotWithInventoryItemAction.rejected, (state) => {
      state.isEnteringPotWithInventoryItem = false;
    });
    builder.addCase(claimFeesAction.pending, (state) => {
      state.isClaimingFees = true;
    });
    builder.addCase(claimFeesAction.fulfilled, (state, { payload }) => {
      state.isClaimingFees = false;
    });
    builder.addCase(claimFeesAction.rejected, (state, { error }) => {
      state.isClaimingFees = false;
    });
  },
});

export const { addNewBid, updateWinner, clearCurrentRoundInfo } =
  jackpotSlice.actions;

export default jackpotSlice.reducer;
